import { User, VenueGroupConfig } from "../types/VenueGroup";

type PersonNameFields = Partial<
  Pick<User, "full_name" | "first_name" | "last_name">
>;

/**
 * Joins the given name parts into a single trimmed display name, mirroring
 * collins-api's full_name computation (trim(first_name + " " + last_name)).
 */
export const formatPersonName = (
  firstName?: string,
  lastName?: string
): string =>
  [firstName, lastName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ");

/**
 * Returns the user's display name: the trimmed full_name when present,
 * otherwise the name derived from first_name and last_name.
 */
export const getUserFullName = ({
  full_name,
  first_name,
  last_name
}: PersonNameFields): string => {
  const fullName = full_name?.trim();
  if (fullName) return fullName;
  return formatPersonName(first_name, last_name);
};

/**
 * Returns a copy of the config with every user's full_name populated,
 * deriving it from first_name/last_name when the payload omits it. The
 * input config and its users are not mutated.
 */
export const withDerivedUserFullNames = (
  config: VenueGroupConfig
): VenueGroupConfig =>
  Array.isArray(config.users)
    ? {
        ...config,
        users: config.users.map((user) => ({
          ...user,
          full_name: getUserFullName(user)
        }))
      }
    : config;
